import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDots, IconPencil, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';

import { DeleteScopeComponentModal, LogoPaper, PageTitle } from '@/components';
import { useScopeComponents } from '@/hooks';
import { DeletionBlockedResponse, ScopeComponent } from '@/types';
import { fulfillmentBadge, isDataLeaf, leafFulfillment, manualLeafSlugs } from '@/utils';

type View = 'report-sections' | 'data-sources';

const VIEW_PARAM = 'view';
const DEFAULT_VIEW: View = 'report-sections';
const VALID_VIEWS = new Set<View>(['report-sections', 'data-sources']);

function parseView(raw: string | null): View {
  return raw && VALID_VIEWS.has(raw as View) ? (raw as View) : DEFAULT_VIEW;
}

export function ScopeComponentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { scopeComponents, isLoading, deleteComponent } = useScopeComponents({
    includeAll: true,
  });

  const view = parseView(searchParams.get(VIEW_PARAM));
  const setView = (next: View) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === DEFAULT_VIEW) {
          params.delete(VIEW_PARAM);
        } else {
          params.set(VIEW_PARAM, next);
        }
        return params;
      },
      { replace: true }
    );
  };

  const componentsBySlug = useMemo(
    () => new Map(scopeComponents.map((c) => [c.slug, c])),
    [scopeComponents]
  );

  const renderFulfillment = (component: ScopeComponent) => {
    // Data leaf: a single Automated/Manual badge.
    if (isDataLeaf(component)) {
      const badge = fulfillmentBadge(leafFulfillment(component));
      return (
        <Tooltip label={badge.title} withArrow>
          <Badge size="sm" variant="light" color={badge.color}>
            {badge.label}
          </Badge>
        </Tooltip>
      );
    }
    // Section: show API (if any automated leaf) and/or Manual (if any manual leaf).
    const manual = manualLeafSlugs(component, componentsBySlug);
    const badges = [];
    if (component.hasApiDependency) {
      badges.push(
        <Tooltip key="api" label="Uses one or more external API data sources" withArrow>
          <Badge size="sm" variant="light" color="blue">
            API
          </Badge>
        </Tooltip>
      );
    }
    if (manual.length > 0) {
      badges.push(
        <Tooltip key="manual" label={`Includes manual steps: ${manual.join(', ')}`} withArrow>
          <Badge size="sm" variant="light" color="orange">
            Manual
          </Badge>
        </Tooltip>
      );
    }
    return badges.length > 0 ? <Group gap={4}>{badges}</Group> : <Text size="sm">—</Text>;
  };

  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ScopeComponent | null>(null);
  const [deletionBlocked, setDeletionBlocked] = useState<DeletionBlockedResponse | null>(null);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);

  const filteredComponents = useMemo(() => {
    const isReportSection = view === 'report-sections';
    return scopeComponents
      .filter((c) => (isReportSection ? c.dependencies.length > 0 : c.dependencies.length === 0))
      .filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.slug.toLowerCase().includes(search.toLowerCase()) ||
          c.description?.toLowerCase().includes(search.toLowerCase())
      );
  }, [scopeComponents, view, search]);

  const handleDelete = (component: ScopeComponent) => {
    setDeleteTarget(component);
    setDeletionBlocked(null);
    deleteHandlers.open();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await deleteComponent(deleteTarget.id);
      deleteHandlers.close();
      setDeleteTarget(null);
    } catch (err) {
      // Check if this is a 409 with deletion blocked details
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed.referenced_by_configs || parsed.referenced_by_components) {
            setDeletionBlocked(parsed as DeletionBlockedResponse);
          }
        } catch {
          // Not a JSON error, already handled by hook notification
        }
      }
    }
  };

  const dependencyLabel = (component: ScopeComponent) => {
    if (component.dependencies.length === 0) {
      return '—';
    }
    return component.dependencies
      .map((d) => (d.type === 'all_sections' ? 'All Sections' : d.scope_component_slug))
      .join(', ');
  };

  const isReportSectionView = view === 'report-sections';
  const addButtonLabel = isReportSectionView ? 'Add Report Section' : 'Add Data Source';
  const addButtonHref = isReportSectionView
    ? '/admin/scope-components/new?type=report-section'
    : '/admin/scope-components/new?type=data-source';

  return (
    <Stack gap="xl">
      <PageTitle
        title="Scope Components"
        subtitle="Manage report scope components and their rendering configuration"
      />

      <Container size="xl" px={0}>
        <LogoPaper>
          <Stack gap="md">
            <SegmentedControl
              value={view}
              onChange={(v) => setView(v as View)}
              data={[
                { value: 'report-sections', label: 'Report Sections' },
                { value: 'data-sources', label: 'Data Sources' },
              ]}
            />

            <Group justify="space-between">
              <TextInput
                placeholder="Search components..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                w={300}
              />
              <Button leftSection={<IconPlus size={16} />} onClick={() => navigate(addButtonHref)}>
                {addButtonLabel}
              </Button>
            </Group>

            {isLoading ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : filteredComponents.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {search ? 'No components match your search.' : 'No components found.'}
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Slug</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Dependencies</Table.Th>
                    <Table.Th>Fulfillment</Table.Th>
                    <Table.Th w={60} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredComponents.map((component) => (
                    <Table.Tr
                      key={component.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/scope-components/${component.id}`)}
                    >
                      <Table.Td>
                        <Text fw={500}>{component.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {component.slug}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {component.description ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{dependencyLabel(component)}</Text>
                      </Table.Td>
                      <Table.Td>{renderFulfillment(component)}</Table.Td>
                      <Table.Td>
                        <Menu shadow="md" width={160} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                            <Menu.Item
                              leftSection={<IconPencil size={14} />}
                              onClick={() => navigate(`/admin/scope-components/${component.id}`)}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => handleDelete(component)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </LogoPaper>
      </Container>

      <DeleteScopeComponentModal
        opened={deleteOpened}
        onClose={() => {
          deleteHandlers.close();
          setDeleteTarget(null);
          setDeletionBlocked(null);
        }}
        onConfirm={confirmDelete}
        componentName={deleteTarget?.name ?? ''}
        deletionBlocked={deletionBlocked}
      />
    </Stack>
  );
}
